import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type ThemeType = 'auto' | 'light' | 'dark';
export type VariantType = 'navbar' | 'floating' | 'minimal';

export interface ThemeChangedEvent {
  theme: ThemeType;
  appliedTheme: 'light' | 'dark';
}

/**
 * Theme Toggle Component with Bootstrap 5.3+ Dark Mode Support
 * Supports: auto (system), light, dark themes with smooth transitions
 * 
 * @element pm-theme-toggle
 * 
 * @property {ThemeType} theme - Current theme: 'auto' | 'light' | 'dark'
 * @property {VariantType} variant - Button style: 'navbar' | 'floating' | 'minimal'
 * @property {boolean} showLabel - Whether to show text labels
 * 
 * @fires theme-changed - Dispatched when theme changes
 */
@customElement('pm-theme-toggle')
export class PmThemeToggle extends LitElement {
  @property({ type: String, reflect: true }) theme: ThemeType = 'auto';
  @property({ type: String, reflect: true }) variant: VariantType = 'navbar';
  @property({ type: Boolean, attribute: 'show-label' }) showLabel: boolean = false;
  
  @state() private _mediaQuery: MediaQueryList | null = null;
  @state() private _componentObserver: MutationObserver | null = null;

  // Disabilita Shadow DOM per usare stili globali (Font Awesome)
  createRenderRoot() {
    return this;
  }

  private _handleSystemThemeChangeRef = this._handleSystemThemeChange.bind(this);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadTheme();
    this._setupMediaQuery();
    this._setupComponentObserver();
    
    // Sincronizza con il tema già applicato nel head per evitare conflitti
    const currentDataTheme = document.documentElement.getAttribute('data-bs-theme');
    if (currentDataTheme && this.theme === 'auto') {
      // Se il tema è auto, non riapplicarlo per evitare flash
      this.requestUpdate();
    } else {
      this._applyTheme(this.theme);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanupMediaQuery();
    this._cleanupComponentObserver();
  }

  /**
   * Carica il tema salvato o usa il default
   */
  _loadTheme() {
    try {
      const saved = localStorage.getItem('padelMatcherTheme');
      this.theme = (saved as ThemeType) || 'auto';
    } catch (e) {
      console.warn('PadelMatcher Theme: Could not load theme preference');
      this.theme = 'auto';
    }
  }

  /**
   * Salva il tema nel localStorage
   */
  _saveTheme(theme: ThemeType): void {
    try {
      if (theme === 'auto') {
        localStorage.removeItem('padelMatcherTheme');
      } else {
        localStorage.setItem('padelMatcherTheme', theme);
      }
    } catch (e) {
      console.warn('PadelMatcher Theme: Could not save theme preference');
    }
  }

  /**
   * Setup del MediaQuery per auto-theme
   */
  _setupMediaQuery() {
    if (window.matchMedia) {
      this._mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      if (this._mediaQuery.addEventListener) {
        this._mediaQuery.addEventListener('change', this._handleSystemThemeChangeRef);
      } else {
        // Fallback per browser più vecchi
        this._mediaQuery.addListener(this._handleSystemThemeChangeRef);
      }
    }
  }

  /**
   * Cleanup del MediaQuery
   */
  _cleanupMediaQuery() {
    if (this._mediaQuery) {
      if (this._mediaQuery.removeEventListener) {
        this._mediaQuery.removeEventListener('change', this._handleSystemThemeChangeRef);
      } else {
        this._mediaQuery.removeListener(this._handleSystemThemeChangeRef);
      }
    }
  }

  /**
   * Setup del MutationObserver per componenti dinamici
   */
  _setupComponentObserver() {
    if (!window.MutationObserver) return;

    this._componentObserver = new MutationObserver((mutations) => {
      let hasNewComponents = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              // Controlla se il nodo aggiunto è un componente Shadow DOM
              if (node.matches && node.matches('pm-match-card, pm-score-modal, pm-tournament-form')) {
                hasNewComponents = true;
              }
              // Controlla anche i discendenti
              const shadowComponents = node.querySelectorAll?.('pm-match-card, pm-score-modal, pm-tournament-form');
              if (shadowComponents?.length > 0) {
                hasNewComponents = true;
              }
            }
          });
        }
      });

      if (hasNewComponents) {
        // Ritarda leggermente per permettere ai componenti di essere completamente inizializzati
        setTimeout(() => this._syncThemeToShadowComponents(), 100);
      }
    });

    this._componentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Cleanup del MutationObserver
   */
  _cleanupComponentObserver() {
    if (this._componentObserver) {
      this._componentObserver.disconnect();
      this._componentObserver = null;
    }
  }

  /**
   * Gestisce i cambiamenti del tema di sistema
   */
  _handleSystemThemeChange(e: MediaQueryListEvent): void {
    if (this.theme === 'auto') {
      this._applySystemTheme(e.matches);
    }
  }

  /**
   * Applica il tema al documento
   */
  _applyTheme(theme: ThemeType): void {
    const htmlElement = document.documentElement;
    
    switch (theme) {
      case 'dark':
        htmlElement.setAttribute('data-bs-theme', 'dark');
        break;
      case 'light':
        htmlElement.setAttribute('data-bs-theme', 'light');
        break;
      case 'auto':
      default:
        const systemPrefersDark = this._mediaQuery?.matches || false;
        this._applySystemTheme(systemPrefersDark);
        break;
    }

    // Sincronizza tema sui componenti con Shadow DOM
    this._syncThemeToShadowComponents();

    // Dispatch evento personalizzato
    this.dispatchEvent(new CustomEvent('theme-changed', {
      detail: { theme, appliedTheme: this._getAppliedTheme() },
      bubbles: true
    }));
  }

  /**
   * Applica il tema di sistema
   */
  _applySystemTheme(isDark: boolean): void {
    document.documentElement.setAttribute('data-bs-theme', isDark ? 'dark' : 'light');
    // Sincronizza anche sui componenti Shadow DOM
    this._syncThemeToShadowComponents();
  }

  /**
   * Sincronizza il tema sui componenti con Shadow DOM
   */
  _syncThemeToShadowComponents(): void {
    const currentTheme = this._getAppliedTheme();
    
    // Trova tutti i componenti pm-match-card e altri componenti che usano Shadow DOM
    const shadowComponents = document.querySelectorAll('pm-match-card, pm-score-modal, pm-tournament-form');
    
    shadowComponents.forEach(component => {
      if (component instanceof HTMLElement) {
        component.setAttribute('data-bs-theme', currentTheme);
      }
    });
  }

  /**
   * Ottiene il tema effettivamente applicato
   */
  _getAppliedTheme() {
    return document.documentElement.getAttribute('data-bs-theme') || 'light';
  }

  /**
   * Cicla al prossimo tema
   */
  _cycleTheme(): void {
    const themes: ThemeType[] = ['auto', 'light', 'dark'];
    const currentIndex = themes.indexOf(this.theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const nextTheme = themes[nextIndex];
    
    this.theme = nextTheme;
    this._saveTheme(nextTheme);
    this._applyTheme(nextTheme);
    
    // Annuncia il cambiamento per screen readers
    this._announceThemeChange(nextTheme);
  }

  /**
   * Annuncia il cambiamento tema per accessibilità
   */
  _announceThemeChange(theme: ThemeType): void {
    const labels = {
      auto: 'Tema automatico attivato',
      light: 'Tema chiaro attivato',
      dark: 'Tema scuro attivato'
    };
    
    const announcement = labels[theme];
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = announcement;
    
    document.body.appendChild(announcer);
    setTimeout(() => {
      if (announcer.parentNode) {
        document.body.removeChild(announcer);
      }
    }, 1000);
  }

  /**
   * Ottiene l'icona per il tema corrente
   */
  _getThemeIcon(theme: ThemeType): string {
    const icons = {
      auto: 'fa-circle-half-stroke',
      light: 'fa-sun',
      dark: 'fa-moon'
    };
    return icons[theme] || icons.auto;
  }

  /**
   * Ottiene l'etichetta per il tema corrente
   */
  _getThemeLabel(theme: ThemeType): string {
    const labels = {
      auto: 'Automatico',
      light: 'Chiaro',
      dark: 'Scuro'
    };
    return labels[theme] || labels.auto;
  }

  /**
   * Ottiene il tooltip per il tema corrente
   */
  _getThemeTooltip(theme: ThemeType): string {
    const tooltips = {
      auto: 'Tema automatico (segue sistema)',
      light: 'Tema chiaro',
      dark: 'Tema scuro'
    };
    return tooltips[theme] || tooltips.auto;
  }

  /**
   * Gestisce il click sul toggle
   */
  _handleClick(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    this._cycleTheme();
  }

  /**
   * Gestisce il keydown per accessibilità
   */
  _handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      this._cycleTheme();
    }
  }

  render() {
    const iconClass = `fas ${this._getThemeIcon(this.theme)}`;
    const label = this._getThemeLabel(this.theme);
    const tooltip = this._getThemeTooltip(this.theme);
    
    // Usa direttamente le classi Bootstrap
    const buttonClasses = this.variant === 'navbar' 
      ? 'btn btn-outline-light custom-btn'
      : this.variant === 'floating'
      ? 'btn btn-light rounded-circle'
      : 'btn btn-outline-secondary';
    
    return html`
      <button
        class="${buttonClasses}"
        type="button"
        aria-label="${tooltip}"
        title="${tooltip}"
        @click="${this._handleClick}"
        @keydown="${this._handleKeyDown}"
      >
        <i class="${iconClass}" aria-hidden="true"></i>
        ${this.showLabel ? html`<span class="ms-2">${label}</span>` : ''}
      </button>
    `;
  }
}

// Esporta per uso in altri moduli
export default PmThemeToggle;